import type {
  Token,
  TokenTransfer,
  Order,
  Portfolio,
  MarketData,
  GovernanceProposal,
  TokenType,
} from '@vaiu/types';
import {
  normalizeBrandCurrencyCode,
  isValidBrandCurrencyCode,
  resolveBrandCurrencyDisplaySymbol,
  BRAND_CURRENCY_CODE_MIN_LEN,
  BRAND_CURRENCY_CODE_MAX_LEN,
} from './brandCurrencyCode';

/**
 * Brand Currency (MeMoney) — **Platform API Reference Manual v2.2.9** (Currency APIs, aligned Mar 2026):
 *
 * **Currency (prefer v1; v2 fallback on 404/405)** — identical handlers per manual:
 * - `POST /api/v1/currency` — create Brand Currency. Body: `code`, `name`, optional `symbol`, `decimals`,
 *   optional **`issuerStartingBalance`** / `startingBalance`. **Do not** send `supply` / `exchangeRates` (ignored).
 * - `GET /api/v1/currency` — list issuer’s Brand rows (`currencies[]`).
 * - `POST /api/v1/currency/details` — fiat `{ code }`, Brand `{ currencyId }` or `{ category:"memoney", issuerId, code }`.
 * - `GET /api/v1/currency/acceptable` — optional Bearer; fiat + caller’s Brand `currencyId`s when JWT valid.
 *
 * **Token resource routes (manual § V2 interaction):** `GET /api/v2/tokens/:identifier` resolves **Brand `currencyId`**;
 * `POST /api/v2/tokens/transfer` for transfers. This service uses **`v2`** for `tokens/*` only; currency calls use **v1** first.
 * Do **not** create Brand currencies via `POST …/tokens/create` with `type: memoney` (rejected).
 */

function isHttp404Or405(e: unknown): boolean {
  const m = e instanceof Error ? e.message : String(e);
  return /\b404\b/.test(m) || /\b405\b/.test(m);
}

/**
 * HTTP surface each app provides by wrapping its `platformApi` singleton.
 */
export interface UnifiedTokenPlatformApi {
  /** Defaults to `any` so API JSON matches prior app-local service typing. */
  get<T = any>(endpoint: string, options?: Record<string, unknown>): Promise<T>;
  post<T = any>(endpoint: string, data?: unknown, options?: Record<string, unknown>): Promise<T>;
  put<T = any>(endpoint: string, data?: unknown, options?: Record<string, unknown>): Promise<T>;
  delete<T = any>(endpoint: string, options?: Record<string, unknown>): Promise<T>;
  getWebSocketUrl(): string;
}

export class UnifiedTokenService {
  private cache: Map<string, { data: any; expires: number }> = new Map();
  /** v1 prefix for search, portfolio, orders, market, governance (see manual migration table where applicable). */
  private apiVersion = 'v1';
  /**
   * Manual documents resource-style token operations under **v2** (`/api/v2/tokens/...`), including
   * Brand **`currencyId`** resolution for `GET …/tokens/:identifier`.
   */
  private readonly tokensApiVersion = 'v2';

  constructor(private readonly platformApi: UnifiedTokenPlatformApi) {}

  /** `POST /api/v1/currency`, then `POST /api/v2/currency` on 404/405. */
  private async postBrandCurrencyCreate(body: Record<string, unknown>): Promise<unknown> {
    try {
      return await this.platformApi.post('/currency', body);
    } catch (e) {
      if (isHttp404Or405(e)) {
        return await this.platformApi.post('v2/currency', body);
      }
      throw e;
    }
  }

  /** `GET /api/v1/currency`, then `GET /api/v2/currency` on 404/405. */
  private async getMyBrandCurrenciesRaw(): Promise<unknown> {
    try {
      return await this.platformApi.get('/currency');
    } catch (e) {
      if (isHttp404Or405(e)) {
        return await this.platformApi.get('v2/currency');
      }
      throw e;
    }
  }

  /** Unwrap `{ currency }`, `{ data }`, `{ result }` nesting from Brand currency APIs. */
  private static unwrapBrandCurrencyRow(response: unknown): Record<string, any> | null {
    const isRow = (o: unknown): o is Record<string, any> =>
      !!o &&
      typeof o === 'object' &&
      typeof (o as Record<string, unknown>).currencyId === 'string';

    let cur: any = response;
    for (let depth = 0; depth < 8 && cur && typeof cur === 'object'; depth++) {
      if (isRow(cur)) return cur;
      if (cur.currency && isRow(cur.currency)) return cur.currency;
      if (cur.data !== undefined) {
        cur = cur.data;
        continue;
      }
      if (cur.result !== undefined) {
        cur = cur.result;
        continue;
      }
      break;
    }
    return null;
  }

  private static extractCurrenciesList(response: unknown): unknown[] {
    const r = response as Record<string, unknown> | null;
    if (!r || typeof r !== 'object') return [];
    if (Array.isArray(r.currencies)) return r.currencies;
    const data = r.data as Record<string, unknown> | undefined;
    if (data && Array.isArray(data.currencies)) return data.currencies;
    if (Array.isArray((r as { results?: unknown }).results)) return (r as { results: unknown[] }).results;
    return [];
  }

  /** Normalize create-token responses: { data }, nested data, or raw token. */
  private static coerceCreatedToken(response: unknown): Token {
    let cur: any = response;
    for (let depth = 0; depth < 5 && cur && typeof cur === 'object'; depth++) {
      if ('metadata' in cur && ('symbol' in cur || 'type' in cur)) {
        return cur as Token;
      }
      if ('data' in cur && cur.data !== undefined) {
        cur = cur.data;
        continue;
      }
      if ('token' in cur && cur.token !== undefined) {
        cur = cur.token;
        continue;
      }
      break;
    }
    throw new Error('Invalid response from create token API');
  }

  // ============= Token Operations =============

  async createToken(params: {
    type: TokenType;
    symbol: string;
    metadata: Record<string, any>;
    /** Omit `maximum` for unlimited cap (platform-dependent). */
    supply?: { initial: number; maximum?: number };
    pricing?: { type: 'fixed' | 'market' | 'auction'; price?: number; currency?: string };
  }): Promise<Token> {
    const response = await this.platformApi.post(`${this.tokensApiVersion}/tokens/create`, params);
    return UnifiedTokenService.coerceCreatedToken(response);
  }

  async getToken(tokenId: string): Promise<Token & { userBalance?: number }> {
    // Check cache first
    const cached = this.getCached(`token:${tokenId}`);
    if (cached) return cached;

    const encodedId = encodeURIComponent(tokenId);
    const response = await this.platformApi.get(`${this.tokensApiVersion}/tokens/${encodedId}`);
    
    // Cache for different durations based on type
    const ttl = response.type === 'listing' ? 60000 : 5000; // 60s for listings, 5s for others
    this.setCache(`token:${tokenId}`, response, ttl);
    
    return response;
  }

  async transfer(params: {
    from: string;
    to: string;
    amount: number;
    type: 'exchange' | 'purchase' | 'payment' | 'mint' | 'burn' | 'transfer';
    metadata?: Record<string, any>;
  }): Promise<TokenTransfer> {
    const response = await this.platformApi.post(`${this.tokensApiVersion}/tokens/transfer`, params);
    
    // Invalidate balance cache
    this.clearCache('portfolio:*');
    this.clearCache('balance:*');
    
    return response;
  }

  // ============= Specialized Transfer Methods =============

  async exchangeCurrency(from: string, to: string, amount: number): Promise<TokenTransfer> {
    // Get current rate
    const rate = await this.getExchangeRate(from, to);
    
    return this.transfer({
      from,
      to,
      amount,
      type: 'exchange',
      metadata: { rate }
    });
  }

  async buyStock(symbol: string, amount: number, orderType: 'market' | 'limit' = 'market', price?: number): Promise<Order> {
    return this.placeOrder({
      tokenId: `S.${symbol}`,
      side: 'buy',
      amount,
      orderType,
      price
    });
  }

  /**
   * Create a **Brand Currency** (`POST /api/v1/currency`).
   * Response shape per manual: `{ success, currency: { currencyId, … }, issuerStartingBalance? }`.
   */
  async createMeMoney(params: {
    name: string;
    /**
     * Issuer-local **code** (JSON `code`). After NFKC + strip: **4–32** chars, **`[A-Z][A-Z0-9_]{3,31}`**,
     * must not match a configured fiat key (server **400**).
     */
    symbol: string;
    /** Optional display symbol for API field `symbol` (any visible characters; capped in UI). If omitted or blank, **`code`** is used. */
    displaySymbol?: string;
    logo?: string;
    /** @deprecated Prefer `creatorStartingBalance`. */
    initialSupply?: number;
    /** Maps to **`issuerStartingBalance`** (or `startingBalance`) on create; omit or `0` to leave issuer balance unchanged. */
    creatorStartingBalance?: number;
  }): Promise<Token> {
    const code = normalizeBrandCurrencyCode(params.symbol);
    if (!isValidBrandCurrencyCode(code)) {
      throw new Error(
        `Currency code must be ${BRAND_CURRENCY_CODE_MIN_LEN}–${BRAND_CURRENCY_CODE_MAX_LEN} characters: start with A–Z, then A–Z, 0–9, or _ only (Platform API rules).`
      );
    }

    const start =
      params.creatorStartingBalance ??
      (params.initialSupply !== undefined ? params.initialSupply : 0);

    if (start < 0 || !Number.isFinite(start) || !Number.isInteger(start)) {
      throw new Error('Starting balance must be a non-negative whole number.');
    }

    const body: Record<string, unknown> = {
      code,
      name: params.name,
      symbol: resolveBrandCurrencyDisplaySymbol(params.displaySymbol, code),
      decimals: 0,
    };
    if (start > 0) {
      body.issuerStartingBalance = start;
    }

    const response = await this.postBrandCurrencyCreate(body);
    const token = UnifiedTokenService.coerceCreateBrandCurrencyResponse(response);
    this.clearCache('token:*');
    this.clearCache('portfolio:*');
    return token;
  }

  /**
   * List Brand Currencies issued by the authenticated user (`GET /api/v1/currency`, v2 fallback).
   * @param options.enrichFromTokenApi — merge `GET /api/v2/tokens/:currencyId` per row (manual: Brand id resolution on v2 token routes).
   */
  async listMyBrandCurrencies(options?: {
    enrichFromTokenApi?: boolean;
  }): Promise<Array<Token & { userBalance?: number }>> {
    try {
      const response: unknown = await this.getMyBrandCurrenciesRaw();
      const rows = UnifiedTokenService.extractCurrenciesList(response);
      let tokens = rows.map((row: unknown) =>
        UnifiedTokenService.brandCurrencyRowToToken(row as Record<string, unknown>)
      );

      if (options?.enrichFromTokenApi && tokens.length > 0) {
        tokens = await UnifiedTokenService.enrichBrandTokensFromTokenApi(this, tokens);
      }
      return tokens;
    } catch (error) {
      console.warn('GET /currency (or v2/currency) failed:', error);
      return [];
    }
  }

  /** Best-effort merge of token API view (`GET /api/v2/tokens/:id`) into Brand list rows. */
  private static async enrichBrandTokensFromTokenApi(
    svc: UnifiedTokenService,
    tokens: Token[]
  ): Promise<Array<Token & { userBalance?: number }>> {
    const results = await Promise.all(
      tokens.map(async (t) => {
        try {
          const full = await svc.getToken(t.id);
          return UnifiedTokenService.mergeBrandListRowWithTokenView(t, full);
        } catch {
          return t as Token & { userBalance?: number };
        }
      })
    );
    return results;
  }

  private static mergeBrandListRowWithTokenView(
    row: Token,
    full: Token & { userBalance?: number }
  ): Token & { userBalance?: number } {
    return {
      ...row,
      userBalance: full.userBalance ?? (row as { userBalance?: number }).userBalance,
      supply: {
        ...row.supply,
        current:
          typeof full.supply?.current === 'number' ? full.supply.current : row.supply.current,
        initial:
          typeof full.supply?.initial === 'number' ? full.supply.initial : row.supply.initial,
        maximum: full.supply?.maximum ?? row.supply.maximum,
        burned: full.supply?.burned ?? row.supply.burned,
      },
      metadata: {
        ...row.metadata,
        ...full.metadata,
        name: full.metadata?.name || row.metadata.name,
        decimals: full.metadata?.decimals ?? row.metadata.decimals,
        logo: full.metadata?.logo || row.metadata.logo,
        brandCurrencyCode:
          full.metadata?.brandCurrencyCode ?? row.metadata.brandCurrencyCode,
        symbol: full.metadata?.symbol ?? row.metadata.symbol,
      },
      status: full.status ?? row.status,
    };
  }

  /**
   * `GET /api/v1/currency/acceptable` — configured fiat codes plus authenticated user’s Brand `currencyId`s.
   */
  async getCurrencyAcceptable(): Promise<{
    all?: string[];
    currencies?: string[];
    default?: string;
  }> {
    try {
      return await this.platformApi.get('/currency/acceptable');
    } catch (e) {
      if (isHttp404Or405(e)) {
        return await this.platformApi.get('v2/currency/acceptable');
      }
      throw e;
    }
  }

  /**
   * `POST /api/v1/currency/details` (fallback v2) — resolve fiat or Brand metadata.
   */
  async postCurrencyDetails(body: {
    code?: string;
    currencyId?: string;
    category?: 'memoney';
    issuerId?: string;
  }): Promise<Record<string, unknown>> {
    try {
      return await this.platformApi.post('/currency/details', body);
    } catch (e) {
      if (isHttp404Or405(e)) {
        return await this.platformApi.post('v2/currency/details', body);
      }
      throw e;
    }
  }

  /**
   * Mint Brand Currency to a holder. `currencyId` is **24-char hex** or legacy **`MM:issuerId:CODE`**.
   */
  async mintMeMoneyToUser(currencyId: string, userId: string, amount: number): Promise<TokenTransfer> {
    if (!Number.isInteger(amount) || amount <= 0) {
      throw new Error('Mint amount must be a positive whole number');
    }
    return this.transfer({
      from: 'SYSTEM',
      to: userId,
      amount,
      type: 'mint',
      metadata: {
        tokenId: currencyId,
        action: 'mint',
      },
    });
  }

  private static brandCurrencyRowToToken(row: Record<string, any>): Token {
    const supply = row.supply && typeof row.supply === 'object' ? row.supply : {};
    const initial = typeof supply.initial === 'number' ? supply.initial : 0;
    const current = typeof supply.current === 'number' ? supply.current : initial;

    const codeStr =
      row.code != null && String(row.code).trim() !== '' ? String(row.code).trim() : '';
    const hasDisplaySymbol =
      row.symbol != null && String(row.symbol).trim() !== '';
    const displaySymbol = hasDisplaySymbol ? String(row.symbol).trim() : codeStr;

    return {
      id: row.currencyId,
      type: 'memoney',
      symbol: displaySymbol,
      metadata: {
        name: row.name,
        symbol: displaySymbol,
        brandCurrencyCode: codeStr || undefined,
        decimals: typeof row.decimals === 'number' ? row.decimals : 0,
        logo: '',
        brand: {
          name: row.name,
          logo: '',
          website: '',
        },
      },
      supply: {
        initial,
        current,
        maximum: typeof supply.maximum === 'number' ? supply.maximum : undefined,
        burned: typeof supply.burned === 'number' ? supply.burned : undefined,
      },
      pricing: {
        type: 'fixed',
        price:
          row.exchangeRates && typeof row.exchangeRates === 'object' && typeof row.exchangeRates.USD === 'number'
            ? row.exchangeRates.USD
            : 0,
        currency: 'USD',
      },
      createdBy: row.issuerId ?? '',
      createdAt: row.createdAt ?? '',
      updatedAt: row.updatedAt ?? '',
      status:
        row.status === 'active' ? 'active' : row.status === 'delisted' ? 'delisted' : 'paused',
    };
  }

  private static coerceCreateBrandCurrencyResponse(response: unknown): Token {
    const row = UnifiedTokenService.unwrapBrandCurrencyRow(response);
    if (row) {
      return UnifiedTokenService.brandCurrencyRowToToken(row);
    }
    throw new Error('Invalid response from POST /api/v1/currency (Brand Currency create)');
  }

  async createListing(params: {
    title: string;
    description: string;
    images: string[];
    price: number;
    currency?: string;
    category?: string;
    location?: { lat: number; lng: number; address: string };
    shipping?: { available: boolean; cost?: number };
  }): Promise<Token> {
    // v2 API uses structured metadata format as per integration guide
    return this.createToken({
      type: 'listing',
      symbol: `LISTING_${Date.now()}`,
      metadata: {
        listing: {
          title: params.title,
          description: params.description,
          category: params.category || 'Other',
          condition: 'new'
        },
        media: {
          images: params.images.map((url, idx) => ({
            url,
            isPrimary: idx === 0
          }))
        },
        location: params.location ? {
          city: 'Unknown',
          state: 'Unknown',
          coordinates: { lat: params.location.lat, lng: params.location.lng }
        } : undefined,
        shipping: params.shipping ? {
          available: params.shipping.available,
          methods: params.shipping.cost ? [
            { name: 'Standard', price: params.shipping.cost, estimatedDays: 5 }
          ] : []
        } : undefined
      },
      supply: {
        initial: 1,
        maximum: 1
      },
      pricing: {
        type: 'fixed',
        price: params.price,
        currency: params.currency || 'USD'
      }
    });
  }

  async purchaseListing(listingId: string, offer?: number): Promise<TokenTransfer> {
    const listing = await this.getToken(listingId);
    const amount = offer || listing.pricing.price || 0;
    
    return this.transfer({
      from: 'USD', // Or user's preferred currency
      to: listingId,
      amount,
      type: 'purchase',
      metadata: {
        listingId,
        shipping: listing.metadata.shipping
      }
    });
  }

  // ============= Search & Discovery =============

  async search(params: {
    q?: string;
    type?: TokenType | 'all';
    category?: string;
    location?: { lat: number; lng: number; radius: number };
    priceMin?: number;
    priceMax?: number;
    sort?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    results: Token[];
    facets: Record<string, any>;
    total: number;
  }> {
    try {
      const response = await this.platformApi.get(`${this.apiVersion}/search`, { params });
      return response;
    } catch (error) {
      console.warn('Search API not available, returning empty results');
      return { results: [], facets: {}, total: 0 };
    }
  }

  async getPortfolio(): Promise<Portfolio> {
    const cached = this.getCached('portfolio:current');
    if (cached) return cached;

    try {
      const response = await this.platformApi.get(`${this.apiVersion}/portfolio`);
      this.setCache('portfolio:current', response, 10000); // Cache for 10s
      return response;
    } catch (error) {
      console.warn('Portfolio API not available, returning mock data');
      // Return mock portfolio data for development
      const mockPortfolio: Portfolio = {
        userId: 'current',
        balances: [],
        totalValue: 0,
        allocations: {
          currency: 0,
          stocks: 0,
          memoney: 0,
          listings: 0
        },
        performance: {
          day: 0,
          week: 0,
          month: 0,
          year: 0
        }
      };
      return mockPortfolio;
    }
  }

  // ============= Order Management =============

  async placeOrder(params: {
    tokenId: string;
    side: 'buy' | 'sell';
    amount: number;
    orderType: 'market' | 'limit' | 'offer';
    price?: number;
    timeInForce?: 'GTC' | 'IOC' | 'day';
    metadata?: Record<string, any>;
  }): Promise<Order> {
    const response = await this.platformApi.post(`${this.apiVersion}/orders`, params);
    this.clearCache('orders:*');
    return response;
  }

  async getOrders(params?: {
    status?: 'open' | 'filled' | 'cancelled';
    tokenId?: string;
    type?: 'exchange' | 'stock' | 'listing';
  }): Promise<Order[]> {
    try {
      const response = await this.platformApi.get(`${this.apiVersion}/orders`, { params });
      return response;
    } catch (error) {
      console.warn('Orders API not available, returning empty array');
      return [];
    }
  }

  async cancelOrder(orderId: string): Promise<void> {
    await this.platformApi.delete(`${this.apiVersion}/orders/${orderId}`);
    this.clearCache('orders:*');
  }

  async updateOrder(orderId: string, updates: { price?: number; amount?: number }): Promise<Order> {
    const response = await this.platformApi.put(`${this.apiVersion}/orders/${orderId}`, {
      action: 'update',
      ...updates
    });
    this.clearCache('orders:*');
    return response;
  }

  // ============= Market Data =============

  async getMarketPrices(symbols: string[]): Promise<MarketData[]> {
    const cacheKey = `prices:${symbols.join(',')}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.platformApi.get(`${this.apiVersion}/market/prices`, {
        params: { symbols: symbols.join(',') }
      });
      
      this.setCache(cacheKey, response, 1000); // Cache for 1s
      return response;
    } catch (error) {
      console.warn('Market prices API not available, returning mock data');
      // Return mock market data for development
      return symbols.map(symbol => ({
        symbol,
        price: Math.random() * 1000 + 50,
        bid: 0,
        ask: 0,
        spread: 0,
        volume24h: Math.random() * 1000000,
        high24h: 0,
        low24h: 0,
        change24h: (Math.random() - 0.5) * 10,
        changePercent24h: (Math.random() - 0.5) * 5,
        timestamp: new Date().toISOString()
      }));
    }
  }

  async getExchangeRate(from: string, to: string): Promise<number> {
    const prices = await this.getMarketPrices([`${from}_${to}`]);
    return prices[0]?.price || 0;
  }

  async subscribeToUpdates(channels: string[], callback: (data: any) => void): Promise<WebSocket> {
    const ws = new WebSocket(`${this.platformApi.getWebSocketUrl()}/stream`);
    
    ws.onopen = () => {
      ws.send(JSON.stringify({
        action: 'subscribe',
        channels
      }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      callback(data);
    };
    
    return ws;
  }

  // ============= Governance =============

  async createProposal(tokenId: string, proposal: {
    title: string;
    description: string;
    type: 'parameter' | 'upgrade' | 'treasury' | 'general';
    changes?: Record<string, any>;
  }): Promise<GovernanceProposal> {
    const response = await this.platformApi.post(`${this.apiVersion}/governance/actions`, {
      tokenId,
      type: 'proposal',
      data: proposal
    });
    return response;
  }

  async vote(tokenId: string, proposalId: string, choice: 'for' | 'against' | 'abstain'): Promise<void> {
    await this.platformApi.post(`${this.apiVersion}/governance/actions`, {
      tokenId,
      type: 'vote',
      data: {
        proposalId,
        choice
      }
    });
  }

  async getGovernance(tokenId: string): Promise<{
    proposals: GovernanceProposal[];
    votes: any[];
    actions: any[];
    userVotingPower: number;
  }> {
    const response = await this.platformApi.get(`${this.apiVersion}/governance/${encodeURIComponent(tokenId)}`);
    return response;
  }

  // ============= Cache Management =============

  private getCached(key: string): any {
    const item = this.cache.get(key);
    if (item && item.expires > Date.now()) {
      return item.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      expires: Date.now() + ttl
    });
  }

  private clearCache(pattern: string): void {
    if (pattern.endsWith('*')) {
      const prefix = pattern.slice(0, -1);
      for (const key of this.cache.keys()) {
        if (key.startsWith(prefix)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.delete(pattern);
    }
  }

  // ============= Utility Methods =============

  formatAmount(amount: number, token: Token): string {
    if (token.type === 'memoney') {
      return String(Math.round(amount));
    }
    const decimals = token.metadata.decimals ?? 2;
    return amount.toFixed(decimals);
  }

  formatPrice(price: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(price);
  }

  getTokenIcon(token: Token): string {
    if (token.metadata.logo) return token.metadata.logo;
    
    switch (token.type) {
      case 'currency':
        return `/icons/currencies/${token.symbol.toLowerCase()}.svg`;
      case 'stock':
        return `/icons/stocks/${token.symbol.replace('S.', '').toLowerCase()}.svg`;
      case 'memoney':
        return token.metadata.logo || '/icons/memoney-default.svg';
      case 'listing':
        return token.metadata.images?.[0] || '/icons/listing-default.svg';
      default:
        return '/icons/token-default.svg';
    }
  }
}

let unifiedTokenServiceSingleton: UnifiedTokenService | null = null;

/** One instance per app bundle; pass the app’s platform API adapter. */
export function getUnifiedTokenService(api: UnifiedTokenPlatformApi): UnifiedTokenService {
  if (!unifiedTokenServiceSingleton) {
    unifiedTokenServiceSingleton = new UnifiedTokenService(api);
  }
  return unifiedTokenServiceSingleton;
}
